import React, { useEffect, useRef, useState } from 'react';
import '../styles/IconButton.css';
import { Tooltip } from './';
import { Exit, MagnifyingGlass } from '../assets/icons';
import { colour } from '../styles/GlobalStyles';
import { DotLottieReact } from '@lottiefiles/dotlottie-react';

function IconButton(props) {
    const size = props.size;
    const icon = props.icon;
    const iconParentClass = `icon-button-element-${size} `;
    const iconClass = `icon-button-${size}`;
    const disabled = props.disabled;
    const clickFunc = props.clickFunc;
    const standardIcon = props.standardIcon;
    const contextTooltip = props.contextTooltip;
    const contextTooltipBG = props.contextTooltipBG;
    const contextTooltipText = props.contextTooltipText;
    const customClass = props.customClass;
    const background = props.background;
    const bgType = props.bgType;
    const expandableSearch = props.expandableSearch === true;
    const searchValue = props.searchValue || '';
    const searchOnChange = props.searchOnChange;
    const searchOnClear = props.searchOnClear;
    const searchPlaceholder = props.searchPlaceholder || 'Search...';
    const searchType = props.searchType || 'text';
    const searchLoading = props.searchLoading || false;
    const searchExpandedWidth = props.searchExpandedWidth || '260px';
    const searchInputAriaLabel = props.searchInputAriaLabel || 'Search';
    const collapseOnBlurEmpty = props.collapseOnBlurEmpty !== false;
    const newClass = customClass === null ? "" : `${customClass} `;
    const bgTypeClass = bgType === null || background === false ? "" : `icon-button-bg-${bgType} `;

    const [isTooltipVisible, setTooltipVisible] = useState(false);
    const [tooltipTimer, setTooltipTimer] = useState(null);
    const [isExpanded, setIsExpanded] = useState(false);
    const searchWrapperRef = useRef(null);
    const searchInputRef = useRef(null);

    useEffect(() => {
        if (expandableSearch && searchValue.trim() !== '') {
            setIsExpanded(true);
        }
    }, [expandableSearch, searchValue]);

    const handleMouseEnter = () => {
        if (contextTooltip) {
            const timer = setTimeout(() => setTooltipVisible(true), 500);
            setTooltipTimer(timer);
        }
    };

    const handleMouseLeave = () => {
        clearTimeout(tooltipTimer);
        setTooltipVisible(false);
    };

    const handleClick = (e) => {
        if (disabled === false && clickFunc) {
            try {
                clickFunc(e);
            } catch (error) {
                console.error("Error in clickFunc:", error);
            }
        }
    };

    const handleMouseDown = (e) => {
        // Prevent blur event on input when clicking button
        // This allows the click to be processed before the input loses focus
        if (!disabled) {
            e.preventDefault();
        }
    };

    const collapseSearch = () => {
        setIsExpanded(false);
    };

    const handleSearchTriggerClick = (e) => {
        if (disabled) return;
        e.preventDefault();
        e.stopPropagation();

        if (!isExpanded) {
            setIsExpanded(true);
            setTimeout(() => searchInputRef.current?.focus(), 0);
            return;
        }

        if (searchValue.trim() === '') {
            collapseSearch();
            return;
        }

        searchInputRef.current?.focus();
    };

    const handleSearchClear = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (searchOnClear) {
            searchOnClear();
        }
        searchInputRef.current?.focus();
    };

    const handleSearchInputBlur = () => {
        if (!collapseOnBlurEmpty) return;
        if (searchValue.trim() !== '') return;
        setTimeout(() => {
            if (!searchWrapperRef.current?.contains(document.activeElement)) {
                collapseSearch();
            }
        }, 0);
    };

    const handleSearchKeyDown = (e) => {
        if (e.key === 'Escape') {
            e.preventDefault();
            if (searchValue.trim() !== '') {
                searchOnClear?.();
                return;
            }
            collapseSearch();
        }
    };

    if (expandableSearch) {
        return (
            <div
                ref={searchWrapperRef}
                className={`icon-button-search-container ${isExpanded ? 'icon-button-search-expanded' : 'icon-button-search-collapsed'} ${newClass}`}
                style={{ '--icon-button-search-width': searchExpandedWidth }}
            >
                <button
                    type="button"
                    className={"icon-button-element icon-button-search-trigger " + (iconParentClass) + (background === true ? (bgTypeClass) : "icon-button-transparent ") + (disabled === false ? "cursor-pointer " : "disabled ") + (contextTooltip === true ? "relative-container" : "")}
                    onClick={handleSearchTriggerClick}
                    onMouseDown={handleMouseDown}
                    onMouseEnter={() => handleMouseEnter()}
                    onMouseLeave={() => handleMouseLeave()}
                    disabled={disabled}
                    aria-label="Toggle search"
                    aria-expanded={isExpanded}
                >
                    <MagnifyingGlass fill={colour.gray06} className="icon-button-search-magnifier" clickFunc={null} />
                    {
                        contextTooltip === true && !isExpanded &&
                        <Tooltip
                            type="context"
                            contextTooltipBg={contextTooltipBG}
                            body={contextTooltipText}
                            visible={isTooltipVisible}
                            exit={false}
                            exitFunc={null}
                        />
                    }
                </button>
                <div className="icon-button-search-input-shell">
                    <input
                        ref={searchInputRef}
                        type={searchType}
                        value={searchValue}
                        onChange={searchOnChange}
                        onBlur={handleSearchInputBlur}
                        onKeyDown={handleSearchKeyDown}
                        className="icon-button-search-input body-regular colour-primary"
                        placeholder={searchPlaceholder}
                        disabled={disabled}
                        aria-label={searchInputAriaLabel}
                    />
                    <div className="icon-button-search-trailing">
                        {searchLoading ? (
                            <div className="icon-button-search-loading">
                                <DotLottieReact
                                    src="https://lottie.host/7f215b0b-e72e-41ed-b9af-4249c3810a00/afvrLLR4JB.lottie"
                                    loop
                                    autoplay
                                />
                            </div>
                        ) : searchValue === '' ? (
                            <MagnifyingGlass
                                fill={colour.gray04}
                                className="icon-button-search-input-icon"
                                clickFunc={null}
                            />
                        ) : (
                            <button
                                type="button"
                                className="icon-button-search-clear cursor-pointer"
                                onMouseDown={(e) => e.preventDefault()}
                                onClick={handleSearchClear}
                                aria-label="Clear search"
                            >
                                <Exit
                                    fill={colour.gray07}
                                    size={8}
                                    className="icon-button-search-input-icon"
                                />
                            </button>
                        )}
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div 
            className={"icon-button-element " + (iconParentClass) + (background === true ? (bgTypeClass) : "icon-button-transparent ") + (newClass) + (disabled === false ? "cursor-pointer " : "disabled ") + (contextTooltip === true ? "relative-container" : "")}
            onClick={handleClick}
            onMouseDown={handleMouseDown}
            onMouseEnter={() => handleMouseEnter()}
            onMouseLeave={() => handleMouseLeave()}
        >
            {
                standardIcon === false ?
                icon
                :
                <img
                    src={icon}
                    className={iconClass}
                    alt="Icon Button"
                />
            }
            {
                contextTooltip === true &&
                <Tooltip
                    type="context"
                    contextTooltipBg={contextTooltipBG}
                    body={contextTooltipText}
                    visible={isTooltipVisible}
                    exit={false}
					exitFunc={null}
                />
            }
        </div>
    )
};

export default IconButton;